/**
 * Tarjetas de resumen KPI (Ingreso / Gastos / Utilidad + variaciones).
 * Optimizadas para vista desktop: valores largos en COP visibles sin
 * solapamiento de labels. Layout: periodo1 arriba-izq | periodo2 arriba-der,
 * variación abajo a todo el ancho.
 */
import { money, pctFmt, varColor } from './comparativosUtils';

interface KpiData {
  label: string;
  periodo1Label: string;
  periodo2Label: string;
  valor1: number;
  valor2: number;
  varAbs: number;
  varPct: number | null;
  tipoCuenta: 'INGRESO' | 'GASTOS' | 'UTILIDAD';
}

function KpiCard({ d }: { d: KpiData }) {
  const colorClass = varColor(d.varAbs, d.tipoCuenta);
  const arrow = d.varAbs >= 0 ? '▲' : '▼';

  return (
    <div className="rounded-xl border border-border bg-surface p-4 shadow-sm">
      {/* Etiqueta de la KPI */}
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-400">
        {d.label}
      </p>

      {/* Dos periodos en grid — cada uno ocupa el ancho completo para que
          los valores no se solapen aunque sean largos. */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {/* Periodo 1 */}
        <div>
          <p className="mb-0.5 text-[11px] font-medium text-ink-400 leading-none">
            {d.periodo1Label}
          </p>
          <p className="text-sm font-semibold text-ink-600 tabular-nums leading-tight">
            {money(d.valor1)}
          </p>
        </div>

        {/* Periodo 2 */}
        <div className="text-right">
          <p className="mb-0.5 text-[11px] font-medium text-ink-400 leading-none">
            {d.periodo2Label}
          </p>
          <p className="text-sm font-bold text-ink-900 tabular-nums leading-tight">
            {money(d.valor2)}
          </p>
        </div>
      </div>

      {/* Divisor */}
      <div className="my-2.5 border-t border-border" />

      {/* Variación */}
      <div className={`flex items-center gap-1.5 text-sm font-semibold ${colorClass}`}>
        <span className="text-base leading-none">{arrow}</span>
        <span className="tabular-nums">{money(Math.abs(d.varAbs))}</span>
        <span className="ml-auto text-xs font-normal opacity-75">
          {pctFmt(d.varPct)}
        </span>
      </div>
    </div>
  );
}

interface Props {
  periodo1Label: string;
  periodo2Label: string;
  ingreso1: number;
  ingreso2: number;
  gastos1: number;
  gastos2: number;
  utilidad1: number;
  utilidad2: number;
  varIngresoAbs: number;
  varIngresoPct: number | null;
  varGastosAbs: number;
  varGastosPct: number | null;
  varUtilidadAbs: number;
  varUtilidadPct: number | null;
}

export function SummaryCards(p: Props) {
  const cards: KpiData[] = [
    {
      label: 'Ingreso',
      periodo1Label: p.periodo1Label,
      periodo2Label: p.periodo2Label,
      valor1: p.ingreso1,
      valor2: p.ingreso2,
      varAbs: p.varIngresoAbs,
      varPct: p.varIngresoPct,
      tipoCuenta: 'INGRESO',
    },
    {
      label: 'Gastos',
      periodo1Label: p.periodo1Label,
      periodo2Label: p.periodo2Label,
      valor1: p.gastos1,
      valor2: p.gastos2,
      varAbs: p.varGastosAbs,
      varPct: p.varGastosPct,
      tipoCuenta: 'GASTOS',
    },
    {
      label: 'Utilidad',
      periodo1Label: p.periodo1Label,
      periodo2Label: p.periodo2Label,
      valor1: p.utilidad1,
      valor2: p.utilidad2,
      varAbs: p.varUtilidadAbs,
      varPct: p.varUtilidadPct,
      tipoCuenta: 'UTILIDAD',
    },
  ];

  return (
    <div className="grid grid-cols-3 gap-4">
      {cards.map((d) => (
        <KpiCard key={d.label} d={d} />
      ))}
    </div>
  );
}
